import React, { ChangeEvent, Dispatch, ReactElement, useEffect, useRef, useState } from 'react'
import { CalenderIcon, DeleteCardIcon, DescriptionIcon, EarlWarningHRW, EditCardIcon, IconHighWind, IdIcon, SearchIcon } from '../iconSet/IconSet'
import { EarlyWarningTypes } from './CreateWarningTypes'
import { earlyWarningTypes } from './FixedDataSet'
import MapComp, { Coordinate } from './MapComp'
import { InputGroupPropsTypes } from '../inputComp/InputGroupType'
import InputGroup from '../inputComp/InputGroup'
import { SelectItemTypes } from '../../CommonTypes'
import { useNavigate } from 'react-router-dom'
import { convertToOffsetDateTime, formatDate, getSeverityColor } from './CommonMethods'
import AlertDialog from '../popupComp/AlertDialog'
import { APIDeleteTemplate, APIEwSubmitTemplate, APIGetAllTemplates, APIRequestSubmitEwTemplate, APITemplateResponse, APIUpdateTemplate } from '../../services/APITemplateService'
import LoadingScreen from '../loadingScreenComp/LoadingScreen'
import { PoliceTypes } from './CreateWarning'

type SelectType = "SELECT" | "TRIGGER" | "EDIT" | "DELETE" | "CREATE"

/* export type TemplateData = {
    type: EarlyWarningTypes 
    name: string;
    description: string;
    severity: string;
} */

type EWData = {
    name: string;
    icon: ReactElement<any, any>;
}

export const dataMap: Map<EarlyWarningTypes, EWData> = new Map(
    earlyWarningTypes.map(ew => [
        ew.wType, {name: ew.wName, icon: ew.icon}
    ])
)

type EWTemplatePropsType = {
    setSelectedTemplate: Dispatch<React.SetStateAction<APITemplateResponse | null>>;
    selectedTemplate: APITemplateResponse | null;
    setToggle: Dispatch<React.SetStateAction<boolean>>;
    isButtonActive: boolean;
}

const EWTemplate = (props: EWTemplatePropsType) => {

    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);  // for OK

    const showAlertDialog = (title: string, description: string, yesCallback?: () => void, okCallback?: () => void ) => {
        setAlertTitle(title);
        setAlertDescription(description);
        setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
        setAlertOkCallback(() => okCallback);   // Will trigger only on OK
        setAlertDialogState(true);
    }

    // Loading screen
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const navigate = useNavigate()
    
    const [filterTemplateData, setFilterTemplateData] = useState<APITemplateResponse[]>([])

    // Template data
    const [templateData, setTemplateDate] = useState<APITemplateResponse[]>([])

    useEffect(() => {
        const getAllTemplates = async () => {
            setIsLoading(true)
            const response = await APIGetAllTemplates()

            if (response.status)
            {
                setTemplateDate(response.data)
                setFilterTemplateData(response.data)
            }
            else 
            {
                console.log("Data fetching error")
            }
            setIsLoading(false)
        }
        getAllTemplates()
    }, [])

    // Handle delete template
    const handleDeleteTemplateClick = async (id: string) => {
        setIsLoading(true)
        const response = await APIDeleteTemplate(id)

        if (response.status)
        {
            showAlertDialog("Success", "✅ Warning template deleted successfully!", undefined, () => window.location.reload());
        }
        else 
        {
            showAlertDialog("Error", `❌ ${response.message}`)
        }
        setIsLoading(false)
    }

    // Handle edit template
    const handleEditTemplateClick = (id: string) => {
        const url = `/early_warning/setup_template?id=${id}`
        navigate(url)
    }

    // Handle click template
    const handleClickTemplate = (item: APITemplateResponse) => {
        const data = item;
        const params = new URLSearchParams({data: JSON.stringify(data)}).toString();
        navigate(`/early_warning/trigger_template?${params}`)
    }

    // Handle search change
    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        if (value === "" || value === null || value === undefined)
        {
            setFilterTemplateData(templateData)
        }
        else 
        {
            setFilterTemplateData(prevState => (
                templateData.filter(item => item.name.toLowerCase().includes(value.toLowerCase()) || item.warningName.toLowerCase().includes(value.toLowerCase()) || dataMap.get(item.type)?.name.toLowerCase().includes(value.toLowerCase()))
            ))
        }
    }

    return (
        <>
            <div className='ew-template-container'>
                {templateData.length > 0 && (
                    <div className='wrapper-header'>
                        <div className='search-form-wrapper'>
                            <div>
                                <SearchIcon width='1.5rem' color='var(--color-main-white)'/>
                            </div>
                            <input type="text" placeholder='Search templates here' onChange={(e) => handleSearchChange(e)}/>
                        </div>
                        <button onClick={(e) => navigate('/early_warning/setup_template')}>+ Create New Template</button>
                    </div>
                )}
                
                {templateData.length > 0 ? (
                    <div className='ew-template-wrapper'>
                        {filterTemplateData.map((item, index) => (
                            <div key={index} className='template-card-wrapper' onClick={(e) => handleClickTemplate(item)}>
                                <div className='icon-wrapper'>
                                    {dataMap.get(item.type)?.icon}
                                </div>
                                <div className='body-wrapper'>
                                    <span className='title'>{dataMap.get(item.type)?.name} Template - ({item.name})</span>
                                    <div className='details-wrapper'>
                                        <div className="cell header">Severity</div>
                                        <div className="cell" style={{width: '100%', height: `100%`, display: 'flex', alignItems: 'center', justifyContent: 'flex-start'}}>
                                            <div style={{ width: '2rem', height: '0.5rem', backgroundColor: `${getSeverityColor(item.severity)}`, borderRadius: '0.2rem'}}></div>
                                            <span style={{marginLeft: '1rem', fontSize: '0.8rem', textTransform: 'capitalize'}}>({item.severity})</span>
                                        </div>
                                        <div className="cell header">Name</div>
                                        <div className="cell">{item.warningName}</div>
                                        <div className="cell header">Description</div>
                                        <div className="cell">{item.description}</div>
                                    </div>
                                    <div className='edit-icon-wrapper'>
                                        <div className='template-ico edit-template-ico' onClick={(e) => {
                                            e.stopPropagation()
                                            handleEditTemplateClick(item.id)
                                        }}>
                                            <EditCardIcon width='1rem' color='var(--color-text)' />
                                        </div>
                                        <div className='template-ico delete-template-ico' onClick={(e) => {
                                                e.stopPropagation()
                                                showAlertDialog(
                                                    "Confirm Delete",
                                                    "Are you sure you want to delete this warning template?",
                                                    () => handleDeleteTemplateClick(item.id), undefined
                                                )
                                            }}>
                                            <DeleteCardIcon width='1rem' color='var(--color-text)' />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className='no-data-wrapper'>
                        <span>No any templates available</span>
                        {props.isButtonActive && (
                            <button onClick={(e) => navigate('/early_warning/setup_template')}>+ Create New Template</button>
                        )}
                        
                    </div>
                )}
            </div>

            {alertDialogState && (
                <AlertDialog title={alertTitle} description={alertDescription} windowState={alertDialogState} setWindowState={setAlertDialogState} function={alertYesCallback} okFunction={alertOkCallback} />
            )}
            
            {isLoading && (
                <LoadingScreen />
            )}
        </>
    )
}

export default EWTemplate